import type { AuthSession } from '@streamr/shared';
import { ApiError, toApiError } from './errors';
import { getSessionId } from '../lib/session';

/**
 * The one place the web app talks to the API.
 *
 * Base path is /api/v1 (supervisor ruling, mesh #117). In dev the Vite server
 * proxies /api to the API process, so this is same-origin and the httpOnly
 * refresh cookie travels without any CORS credentials negotiation. In
 * production VITE_API_BASE_URL can point elsewhere, and `credentials:'include'`
 * keeps the cookie working if it does.
 */
/**
 * `/api/v1` is the ruled prefix (mesh #117) and the one the API's own route
 * modules are written for: routes/discovery/index.ts documents a `/api/v1`
 * mount and routes/discovery/queries.ts hardcodes `/api/v1/videos/:id/...` into
 * the poster URLs it returns.
 *
 * It is overridable ONLY so a deployment can move the API, not as a way of
 * living with a server that mounts something else. As of this commit
 * apps/api/src/app.ts:63 mounts each route folder at `/api/<folder>`, which
 * matches neither module's expectation -- reported on the mesh; the fix belongs
 * in the mount, not here.
 */
export const API_PREFIX = import.meta.env.VITE_API_PREFIX ?? '/api/v1';

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

export const apiUrl = (path: string): string => `${BASE_URL}${API_PREFIX}${path}`;

/* --------------------------------------------------------------------------
 * Access token holder
 *
 * The access token lives in memory only, never in localStorage: it is a bearer
 * credential and localStorage is readable by any XSS. Durability comes from the
 * httpOnly refresh cookie instead -- on a cold load the app calls /auth/refresh
 * once and is signed in again.
 * ----------------------------------------------------------------------- */

type TokenListener = (token: string | null) => void;

let accessToken: string | null = null;
const listeners = new Set<TokenListener>();

export const getAccessToken = (): string | null => accessToken;

export const setAccessToken = (token: string | null): void => {
  accessToken = token;
  for (const listener of listeners) listener(token);
};

export const onAccessTokenChange = (listener: TokenListener): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/* --------------------------------------------------------------------------
 * Refresh, single-flight
 *
 * A watch page can have six requests in flight when the 15-minute access token
 * expires. Without a single-flight guard each one would POST /auth/refresh, and
 * because refresh tokens ROTATE on every use (auth contract, mesh #86/#108),
 * the second rotation would be seen by the server as reuse of an already
 * rotated token -- which by design revokes the entire family and signs the user
 * out. So concurrent refreshes are not merely wasteful here, they are a logout
 * bug. Every caller awaits the same promise.
 * ----------------------------------------------------------------------- */

let refreshInFlight: Promise<string | null> | null = null;

const requestRefresh = async (): Promise<string | null> => {
  try {
    const response = await fetch(apiUrl('/auth/refresh'), {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-Session-Id': getSessionId() },
    });
    if (!response.ok) {
      setAccessToken(null);
      return null;
    }
    const session = (await response.json()) as AuthSession;
    setAccessToken(session.accessToken);
    return session.accessToken;
  } catch {
    setAccessToken(null);
    return null;
  }
};

export const refreshAccessToken = (): Promise<string | null> => {
  refreshInFlight ??= requestRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
};

/* --------------------------------------------------------------------------
 * Request
 * ----------------------------------------------------------------------- */

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Raw body (upload parts). Skips JSON encoding. */
  rawBody?: BodyInit;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Set false for endpoints that must not trigger a refresh (e.g. /auth/*). */
  retryOnUnauthorized?: boolean;
}

const buildQuery = (query: RequestOptions['query']): string => {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const encoded = params.toString();
  return encoded === '' ? '' : `?${encoded}`;
};

const send = async (path: string, options: RequestOptions, token: string | null): Promise<Response> => {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Session-Id': getSessionId(),
    ...options.headers,
  };
  if (token !== null) headers.Authorization = `Bearer ${token}`;

  let body: BodyInit | undefined;
  if (options.rawBody !== undefined) {
    body = options.rawBody;
  } else if (options.body !== undefined) {
    body = JSON.stringify(options.body);
    headers['Content-Type'] = 'application/json';
  }

  return fetch(`${apiUrl(path)}${buildQuery(options.query)}`, {
    method: options.method ?? 'GET',
    credentials: 'include',
    headers,
    body,
    signal: options.signal,
  });
};

/**
 * Performs the request, and on a 401 refreshes once and replays it. Exactly
 * once: a second 401 after a successful refresh means the request is genuinely
 * unauthorized, and retrying again would loop.
 */
export const request = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
  const retry = options.retryOnUnauthorized ?? true;

  let response: Response;
  try {
    response = await send(path, options, getAccessToken());
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError(0, 'NETWORK', 'Could not reach Streamr.', cause);
  }

  if (response.status === 401 && retry) {
    const refreshed = await refreshAccessToken();
    if (refreshed !== null) {
      try {
        response = await send(path, options, refreshed);
      } catch (cause) {
        if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
        throw new ApiError(0, 'NETWORK', 'Could not reach Streamr.', cause);
      }
    }
  }

  if (!response.ok) throw await toApiError(response);

  // 204 and empty bodies are legitimate for DELETE / mark-read style routes.
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  if (text === '') return undefined as T;
  return JSON.parse(text) as T;
};

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};
